import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Axios from '@/shared/api/axios';
import { useSelector } from 'react-redux';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import ReportModal from '@/components/ui/ReportModal';
import LoadingButton from '@/components/ui/LoadingButton';
import { useTranslation } from 'react-i18next';
import Loading from '@/components/ui/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faBan } from '@fortawesome/free-solid-svg-icons';
import { getContentItemId } from '@/shared/utils/contentItemId';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { formatCount } from '@/shared/utils/formatNumber';

import placeholder_portrait_200x200 from "@/assets/imgs/placeholders/portrait_200x200.jpg";
import placeholder_cover_1600x700 from "@/assets/imgs/placeholders/cover_1600x700.jpg";


import {
    Row,
    Col,
    Container,
    Dropdown,
} from 'react-bootstrap';

import '@/assets/css/style.css'

import EllipsisToggle from '@/components/ui/EllipsisToggle';
import MainNav from '@/components/layout/AppNavbar';
import PostCard from '@/features/posts/components/PostCard';
import Introduction from '@/features/idol/components/Introduction';
import TabIdol from '@/features/idol/components/FollowedIdols';
import TabWorld from '@/features/idol/components/FollowedWorlds';
import TabMedia from '@/features/idol/components/IdolMedia';
import SubscriptionPlanModal from '@/features/subscription/components/SubscriptionPlanModal';
import SubscriptionConfirmModal from '@/features/subscription/components/SubscriptionConfirmModal';
import BrokenMedia from '@/components/shared/BrokenMedia';
import IdolAvatar from '@/components/ui/IdolAvatar';
import IdolName from '@/components/ui/IdolName';



function IdolAccountList() {
    const { t } = useTranslation();

    const params = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const modalFollowSuccess = useRef(null);
    const modalBlockSuccess = useRef(null);
    const modalBlockConfirm = useRef(null);
    const modalSubscribeSuccess = useRef(null);
    const statusModalRef = useRef(null);
    const reportModalRef = useRef(null);
    const tabBarRef = useRef(null);

    const idolId = useSelector((state) => state.user.idolId);
    const userId = useSelector((state) => state.user.userId);
    const chatImageUrl = useSelector((state) => state.chat.imageUrl);

    const [idolInfo, setIdolInfo] = useState({});
    const [isFollow, setIsFollow] = useState(false);
    const rawTab = searchParams.get('tab');
    const activeTab = ['media', 'idol', 'world'].includes(rawTab) ? rawTab : 'post';
    const [followMessage, setFollowMessage] = useState('');

    // 訂閱相關狀態
    const [showSubscriptionPlanModal, setShowSubscriptionPlanModal] = useState(false);
    const [showSubscriptionConfirmModal, setShowSubscriptionConfirmModal] = useState(false);
    const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState(null);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);
    const [currentStatusCode, setCurrentStatusCode] = useState('');
    // 偶像資料無法查看（已停權或已刪除）：StatusModal 關閉後導回首頁。
    // 只有載入偶像資料失敗時設定，追隨、封鎖等操作失敗仍留在原頁
    const [isIdolUnavailable, setIsIdolUnavailable] = useState(false);
    const [coverError, setCoverError] = useState(false);

    const goHome = () => navigate('/', { replace: true });

    // 目前正在查看的偶像 id（= view_idol_id），與 idolInfo 分開追蹤，
    // 好在切換偶像的瞬間同步清空，避免舊偶像的殘留請求在新的 fetchIdolInfo resolve 前被誤發出去
    const viewIdolIdRef = useRef(null);

    // 防止切換偶像時，前一個偶像較晚 resolve 的 get-idol-info 蓋掉目前偶像的狀態
    // （例如新偶像回 IDOL_3044 時，畫面會留著前一個偶像的資料）
    const idolInfoSeqRef = useRef(0);

    const fetchIdolInfo = async () => {
        const seq = ++idolInfoSeqRef.current;
        try {
            const idolResponse = await Axios.post('/idol/get-idol-info', {
                me_idol_id: idolId,
                idol_uuid: params.idolUuid,
            });
            if (seq !== idolInfoSeqRef.current) return;

            // 只有成功才寫入 idolInfo；其他狀態碼（例如 IDOL_3044 偶像已停權或刪除）不會帶 data，
            // 寫進去會讓 render 讀 idolInfo.id 時整頁崩潰
            switch (idolResponse.data.status_code) {
                case 'SYSTEM_1000': {
                    const data = idolResponse.data.data;
                    setIdolInfo(data);
                    setIsFollow(data.is_follow);
                    viewIdolIdRef.current = data.id;
                    break;
                }
                case 'SYSTEM_1001':
                    navigate('/404', { replace: true });
                    break;
                case 'IDOL_3044':
                case 'IDOL_3045':
                    // 偶像已停權或已刪除（3044：自己帳號底下的偶像；3045：別人的偶像）：
                    // 頁面沒有可顯示的內容，提示關閉後導回首頁，不留在空白頁。
                    // 3044 若是目前操作的偶像被停權，StatusModal 由全站鎖定 modal 接手，不會觸發 onClose。
                    // ref 為 null 代表回應到之前已離開頁面（例如被鎖定流程導到 /idol-list、或使用者換頁），不再導回首頁
                    setIsIdolUnavailable(true);
                    setCurrentStatusCode(idolResponse.data.status_code);
                    if (statusModalRef.current && !statusModalRef.current.show()) goHome();
                    break;
                default:
                    setCurrentStatusCode(idolResponse.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            if (seq !== idolInfoSeqRef.current) return;
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Get idol info error:', error);
            }
        }
    };

    // 取得該偶像的貼文列表（單一頁），交由 usePaginatedList 處理分頁狀態與去重 append
    const fetchPage = async (pageNum) => {
        const viewIdolId = viewIdolIdRef.current;
        if (!viewIdolId) return null;
        try {
            const res = await Axios.post('/post/get-list', {
                idol_id: idolId,
                view_idol_id: viewIdolId,
                page: pageNum,
                limit: 10
            });
            // 已切換到其他偶像：舊偶像的貼文不採用。usePaginatedList 的序號只擋得住「之後有新請求」的情況，
            // 新偶像載入失敗時不會再發貼文請求，舊回應會被寫進新偶像的頁面
            if (viewIdolId !== viewIdolIdRef.current) return null;
            if (res.data.status_code !== 'SYSTEM_1000') {
                setCurrentStatusCode(res.data.status_code);
                statusModalRef.current?.show();
                return null;
            }
            return { data: res.data.data, total: res.data.total };
        } catch (error) {
            if (viewIdolId !== viewIdolIdRef.current) return null;
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Fetch idol post list error: ', error);
            }
            return null;
        }
    };

    const { data: postInfo, setData: setPostInfo, isLoading, reset } = usePaginatedList({
        fetchPage,
        getId: getContentItemId,
        limit: 10,
    });

    // 點擊 tags 導向首頁標籤模式
    const updateListWithTag = (tag) => {
        navigate(`/?tags=${tag}`);
    };

    // 本地更新/移除單一貼文或世界觀，取代整包 refetch（避免按讚/收藏/留言等高頻互動時列表跳動）
    // updater 回傳 null/undefined 代表移除該筆
    const patchItem = (itemId, updater) => {
        setPostInfo(prev => prev.reduce((acc, item) => {
            const currentId = getContentItemId(item);
            if (currentId !== itemId) {
                acc.push(item);
                return acc;
            }
            const next = updater(item);
            if (next) acc.push(next);
            return acc;
        }, []));
    };


    // 封鎖偶像：送出後鎖住確認 modal，回應後才關閉並顯示結果
    const blockIdol = async () => {
        setIsBlocking(true);
        try {
            const res = await Axios.post("/idol/create-block", {
                idol_id: idolId,
                block_idol_id: idolInfo.id
            });
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    modalBlockSuccess.current.show();
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsBlocking(false);
            modalBlockConfirm.current?.hide();
        }
    }

    // 追隨/取消追隨偶像
    const followIdol = async () => {
        try {
            const res = await Axios.post("/idol/follow-and-unfollow", {
                idol_id: idolId,
                follow_idol_id: idolInfo.id // 使用從 get-idol-info 取得的數字 ID
            });
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    // 根據當前狀態設定對應的訊息
                    if (isFollow) {
                        setFollowMessage(t('idol_unfollowed'));
                    } else {
                        setFollowMessage(t('idol_followed'));
                    }
                    setIsFollow(!isFollow);
                    modalFollowSuccess.current.show();
                    // 重新載入偶像資訊以更新關注者數
                    fetchIdolInfo();
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Follow idol error:', error);
            }
        }
    }

    // 傳送訊息，導向聊天室
    const handleSendMessage = () => {
        navigate(`/chats/${params.idolUuid}`, {
            state: {
                receiverIdolInfo: {
                    idol_full_name: idolInfo.idol_full_name,
                    idol_nick_name: idolInfo.idol_nick_name,
                    avatar_path: chatImageUrl ? idolInfo.idol_avatar_url?.replace(chatImageUrl, '') : null,
                    idol_id: idolInfo.id,
                    user_id: idolInfo.user_id,
                    uuid: params.idolUuid,
                    is_official: idolInfo.is_official
                }
            }
        });
    };

    const handleTab = (tab) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (tab === 'post') {
                next.delete('tab');
            } else {
                next.set('tab', tab);
            }
            return next;
        }, { replace: true });
        // post tab 的資料在進頁面時就已預先載入，可以直接捲動；
        // media/idol/world tab 是切換當下才 fetch，要等各自元件資料到位後才能捲（見各元件內的捲動邏輯），
        // 這裡先捲會因為資料還沒回來、內容比 viewport 短，被瀏覽器強制 clamp 回頂端
        if (tab === 'post') {
            tabBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    // 訂閱相關處理函數
    const handleSubscribeClick = () => {
        setShowSubscriptionPlanModal(true);
    };

    const handlePlanConfirm = (plan) => {
        setSelectedSubscriptionPlan(plan);
        setShowSubscriptionPlanModal(false);
        setShowSubscriptionConfirmModal(true);
    };

    // 錢包餘額抓不到就關掉扣款 modal 並顯示錯誤碼：餘額未知時不該讓使用者繼續走扣款流程
    const handleSubscribeWalletError = useCallback((statusCode) => {
        setShowSubscriptionConfirmModal(false);
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
    }, []);

    const handleSubscriptionConfirm = async () => {
        if (!idolId || !idolInfo.id || !selectedSubscriptionPlan) return;

        // subscribe-idol 回應較慢，送出後鎖住確認 modal 並顯示 spinner
        setIsSubscribing(true);
        try {
            const response = await Axios.post('/idol/subscribe-idol', {
                idol_id: idolId,
                view_idol_id: idolInfo.id,
                plan_duration: selectedSubscriptionPlan.duration
            });

            if (response.data?.status_code === 'SYSTEM_1000') {
                setShowSubscriptionConfirmModal(false);
                modalSubscribeSuccess.current.show();
                // 重新載入偶像資訊以更新訂閱狀態
                fetchIdolInfo();
            } else if (response.data?.status_code) {
                setShowSubscriptionConfirmModal(false);
                setCurrentStatusCode(response.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            setShowSubscriptionConfirmModal(false);
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Failed to subscribe:', error);
            }
        } finally {
            setIsSubscribing(false);
        }
    };

    useEffect(() => {
        // idolId（自己的偶像 id）或所查看偶像的 uuid 變動時，都要重新載入
        // 從偶像 A 頁面點推播導向偶像 B 時，元件不會重新掛載，只有 params.idolUuid 改變
        if (idolId) {
            // 切換偶像前先重置前一個偶像殘留的狀態，避免資料混在一起
            // viewIdolIdRef 同步清空，避免 fetchIdolInfo 完成前的無限滾動誤用前一個偶像的 id 發出請求
            // idolInfo 也要清空，否則新偶像載入失敗（例如已停權）時，畫面會留著前一個偶像的資料
            viewIdolIdRef.current = null;
            setIdolInfo({});
            // 前一個偶像的錯誤提示（例如已停權）不能留在新偶像的頁面上
            statusModalRef.current?.hide();
            setIsIdolUnavailable(false);
            setIsFollow(false);
            setPostInfo([]);
            setCoverError(false);
            fetchIdolInfo();
        }
    }, [idolId, params.idolUuid]);

    // idolInfo.id（被查看偶像的 id，從 get-idol-info 取得）就緒或變動時，重新從第一頁載入貼文
    useEffect(() => {
        if (idolInfo.id) {
            reset();
        }
    }, [idolInfo.id]);


    return (
        <>
            <MainNav />
            {/* 偶像詳細頁 (自己的主頁 對方的主頁共用) */}
            <Container fluid="lg" className="idol-main-page nav-padding">
                {/* <Outlet /> */}

                <Row className="mx-auto gap-4 gap-md-0">
                    <Col lg="12" md="12" sm="12" className="px-0">
                        <div className="cover-pic img-cover">
                            {coverError
                                ? <BrokenMedia className="w-100 h-100" />
                                : <img src={idolInfo.idol_big_cover_url || placeholder_cover_1600x700} onError={() => setCoverError(true)} />
                            }
                        </div>

                        {/* identity：頭像＋名稱。桌機並排（名稱靠頭像底端），手機名稱改置頭像下方。
                            頭像以負 margin 浮上 cover、重疊半個頭像高，名稱再長只會往下延伸不會蓋到按鈕 */}
                        <div key={idolInfo.id} className="ps-2 idol-identity d-flex flex-column flex-sm-row align-items-sm-start gap-2 gap-sm-3 ">
                            {/* 圖片載入失敗由 SafeImg 換成 BrokenMedia；外層以 idolInfo.id 為 key，換偶像時重新掛載會重置 */}
                            <IdolAvatar
                                size="lg"
                                src={idolInfo.idol_big_avatar_url || placeholder_portrait_200x200}
                                idolId={idolInfo.id}
                                imgClassName="border border-white border-4"
                                alt="User Avatar"
                            />
                            <div className="min-w-0 idol-name-block">
                                {/* 等 idolInfo 到位才渲染；is_official 為 0/1 數字，轉成 bool 避免渲染出「0」 */}
                                {!!idolInfo.id &&
                                    <IdolName name={idolInfo.idol_nick_name} isOfficial={!!idolInfo.is_official} size="lg" />
                                }
                                <p className="mb-0 text-gray break-anywhere">{idolInfo.idol_full_name}</p>
                                {/* 關注者／訂閱者數：接在名稱下方，往下延伸不影響下一行按鈕。
                                    等 idolInfo 到位才渲染，避免載入中先閃一列 undefined；兩個欄位後端保證必回傳，
                                    前端不補 || 0 之類的預設值，欄位沒回來就會顯示 undefined，藉此暴露後端缺欄位 */}
                                {!!idolInfo.id &&
                                    <div className="d-flex flex-wrap gap-3 mt-1 fs-14 text-gray">
                                        <span><span className="fw-bold text-dark">{formatCount(idolInfo.follower_count)}</span> {t('followers')}</span>
                                        <span><span className="fw-bold text-dark">{formatCount(idolInfo.subscriber_count)}</span> {t('subscribers')}</span>
                                    </div>
                                }
                            </div>
                        </div>

                        {/* 按鈕行：獨立一行。桌機左右分布（左 follow/subscribe、右 訊息/編輯/…），
                            手機每顆全寬各自一行；… 維持圓形 icon 置於訊息/編輯按鈕行末。
                            等 idolInfo 到位才渲染：載入中或載入失敗時不能出現操作按鈕，否則會帶著 undefined 的 id 打 API */}
                        {!!idolInfo.id &&
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 mt-3 mb-3">
                            {/* 追隨按鈕（非自己偶像） */}
                            {idolId !== idolInfo.id &&
                                <button className={isFollow ? "btn btn-width btn-neutral rounded-pill" : "btn btn-width btn-primary rounded-pill"} onClick={followIdol}>
                                    {isFollow ? t('unfollow_idol') : t('follow_idol')}
                                </button>
                            }
                            {/* 訂閱按鈕（非自己偶像） */}
                            {idolId !== idolInfo.id &&
                                <button
                                    className={idolInfo.subscription_status === 'active' ? "btn btn-width btn-primary rounded-pill btn-subscribed" : "btn btn-width btn-primary rounded-pill"}
                                    onClick={handleSubscribeClick}
                                >
                                    {idolInfo.subscription_status === 'active' ? t('subscribed') : t('subscribe')}
                                </button>
                            }

                            {/* 右群：桌機靠右；手機 訊息/編輯 佔滿剩餘寬度，… 圓形 icon 同行置末 */}
                            <div className="d-flex align-items-center gap-2 ms-sm-auto">
                                {/* 發送訊息按鈕 */}
                                {idolId !== idolInfo.id && idolInfo.chat_enabled &&
                                    <button className="btn btn-width flex-grow-1 flex-sm-grow-0 btn-outline-primaryF rounded-pill" onClick={handleSendMessage}>{t('send_message')}</button>
                                }
                                {/* 編輯偶像按鈕 */}
                                {idolId === idolInfo.id &&
                                    <Link to={`/idol-update/${params.idolUuid}`} className="link-none flex-grow-1 flex-sm-grow-0">
                                        <button className="btn btn-width w-100 btn-primaryF rounded-pill">{t('editIdol')}</button>
                                    </Link>
                                }
                                {/* 更多操作 dropdown（非自己偶像） */}
                                {userId !== idolInfo.user_id &&
                                    <Dropdown>
                                        <Dropdown.Toggle as={EllipsisToggle} buttonClassName="bg-white" />
                                        <Dropdown.Menu align="end">
                                            <Dropdown.Item as="button" onClick={() => modalBlockConfirm.current.show()}>
                                                <FontAwesomeIcon icon={faBan} className="me-2" />
                                                {t('block_idol')}
                                            </Dropdown.Item>
                                            <Dropdown.Item as="button" onClick={() => reportModalRef.current?.show(idolInfo.id)}>
                                                <FontAwesomeIcon icon={faFlag} className="me-2" />
                                                {t('report', { target: t('idol') })}
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                }
                            </div>
                        </div>
                        }
                    </Col>

                    <Col sm="12" className="px-0">
                        <div ref={tabBarRef} className="bg-white rounded border shadowA mb-3 idol-tab-bar">
                                <div className="d-flex">
                                    <h5 className={`p-3 mb-0 hover-p d-flex align-items-center gap-2 ${activeTab === 'post' ? 'tab-active' : ''}`} onClick={() => handleTab('post')}>
                                        <FontAwesomeIcon icon={['fas', 'rectangle-list']} />
                                        <span className="d-none d-md-inline">{t('tab-post')}</span>
                                    </h5>
                                    <h5 className={`p-3 mb-0 hover-p d-flex align-items-center gap-2 ${activeTab === 'media' ? 'tab-active' : ''}`} onClick={() => handleTab('media')}>
                                        <FontAwesomeIcon icon={['fas', 'photo-film']} />
                                        <span className="d-none d-md-inline">{t('tab-media')}</span>
                                    </h5>
                                    <h5 className={`p-3 mb-0 hover-p d-flex align-items-center gap-2 ${activeTab === 'idol' ? 'tab-active' : ''}`} onClick={() => handleTab('idol')}>
                                        <FontAwesomeIcon icon={['fas', 'universal-access']} />
                                        <span className="d-none d-md-inline">{t('tab-idol')}</span>
                                    </h5>
                                    <h5 className={`p-3 mb-0 hover-p d-flex align-items-center gap-2 ${activeTab === 'world' ? 'tab-active' : ''}`} onClick={() => handleTab('world')}>
                                        <FontAwesomeIcon icon={['fas', 'earth-americas']} />
                                        <span className="d-none d-md-inline">{t('tab-world')}</span>
                                    </h5>
                                </div>
                            </div>

                            {activeTab === 'post' &&
                                <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-start">
                                    {/* 側邊欄 - lg 以上 flex:1 min-width:286，lg 以下 max 634 置中（與 main 同寬） */}
                                    <aside className="page-layout-left mx-auto mx-lg-0">
                                        <div className="rounded border bg-white shadowA p-3 idol-main-aside mb-4">
                                            <Introduction idolInfo={idolInfo} />
                                        </div>
                                    </aside>
                                    {/* 動態消息 - lg 以上固定 634，lg 以下置中 */}
                                    <section className="page-layout-main activity-page mx-auto mx-lg-0">
                                        <div className="activity-items">
                                            {postInfo.length > 0 ? (
                                                // /post/get-list 只回傳貼文（idol_info + post_info）
                                                postInfo.map((item) => {
                                                    const itemId = getContentItemId(item);
                                                    return (
                                                        <PostCard
                                                            key={itemId}
                                                            data={item}
                                                            contentType="post"
                                                            onItemChange={(updater) => patchItem(itemId, updater)}
                                                            updateList={reset}
                                                            onTagClick={updateListWithTag}
                                                        />
                                                    );
                                                })
                                            ) : (<div className="text-center">{t("no_post_data")}</div>)
                                            }
                                        </div>
                                    </section>
                                </div>
                            }

                        {activeTab === 'media' && <TabMedia viewIdolId={idolInfo.id} />}

                        {activeTab === 'idol' && <TabIdol viewIdolId={idolInfo.id} />}

                        {activeTab === 'world' && <TabWorld viewIdolId={idolInfo.id} />}
                    </Col>


                </Row>
            </Container>
            <MessageModal ref={modalBlockSuccess} status="success" onClose={() => navigate('/')}>
                <p>{t('idol_blocked')}</p>
            </MessageModal>
            <MessageModal ref={modalBlockConfirm} status="warning" size="md" locked={isBlocking}>
                <h5 className="mb-5">{t('block_confirm')}</h5>
                <div className="text-end">
                    <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => modalBlockConfirm.current.hide()} disabled={isBlocking}>{t('cancel')}</button>
                    <LoadingButton className="btn btn-primary rounded-pill" onClick={blockIdol} isLoading={isBlocking}>{t('confirm')}</LoadingButton>
                </div>
            </MessageModal>
            <MessageModal ref={modalFollowSuccess} status="success">
                <p>{followMessage}</p>
            </MessageModal>
            <MessageModal ref={modalSubscribeSuccess} status="success">
                <p>{t('subscription_success')}</p>
            </MessageModal>

            {/* 訂閱方案選擇 Modal */}
            <SubscriptionPlanModal
                show={showSubscriptionPlanModal}
                onHide={() => setShowSubscriptionPlanModal(false)}
                onConfirm={handlePlanConfirm}
                mode="subscribe"
                idolId={idolInfo.id}
                idolName={idolInfo.idol_nick_name}
            />

            {/* 訂閱確認 Modal */}
            <SubscriptionConfirmModal
                show={showSubscriptionConfirmModal}
                onHide={() => setShowSubscriptionConfirmModal(false)}
                onConfirm={handleSubscriptionConfirm}
                onError={handleSubscribeWalletError}
                mode="subscribe"
                idolName={idolInfo.idol_nick_name}
                selectedPlan={selectedSubscriptionPlan}
                isLoading={isSubscribing}
            />

            {/* 舉報偶像 Modal */}
            <ReportModal ref={reportModalRef} targetKey="idol" />

            {/* Status Modal */}
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} onClose={isIdolUnavailable ? goHome : undefined} />

            {isLoading && <Loading></Loading>}
        </>
    );
}

export default IdolAccountList;